import { getFragmentData } from "@/__generated__";
import { VisitClientFragmentFragmentDoc } from "@/__generated__/graphql";
import { useBookingRouterInterceptor } from "@/components/hooks/use-booking-route";
import { useParams } from "@/components/hooks/use-params";
import type { VisitDetailData } from "@/components/shared/visits/visit-detail";
import { mapToVisitDetailData } from "@/components/shared/visits/visit-detail";
import { useClientReviewsQuery } from "@/gqlhooks/booking/useClientReviewsQuery";
import { getReviewServiceDepartment } from "@/lib/booking-lib";
import { formatDate } from "@/lib/helpers/date";
import { useClientConfirmReservationStore } from "@/store/booking/clientConfirmReservation";
import type { AddToCalendarProps } from "@/store/booking/useBookingState";
import { useBookingState } from "@/store/booking/useBookingState";
import type { Review } from "@/types/booking";
import { type ServiceName } from "@/types/service";
import { PackageRecurrenceEnum } from "@/__generated__/graphql";
import { addYears } from "date-fns";

import { BookingCompleteScreen } from "./booking-complete-screen";

export type BookingCompleteScreenProps = {
  calendarEvent: AddToCalendarProps;
  reviews: Review[];
  cleanerInfo: {
    name: string;
    star: string;
    avatar: string;
  };
  visits: VisitDetailData[];
  isLoading?: boolean;
  isAdHoc?: boolean;
};

export const BookingComplete = () => {
  useBookingRouterInterceptor();

  const { name = "home-cleaning" } = useParams<{ name: ServiceName }>();

  const { bookingInfo, selectedSlot } = useBookingState((state) => state.data);

  const { data: confirmationData, loading: confirmationLoading } =
    useClientConfirmReservationStore();

  const { list: reviewsList, loading: reviewsLoading } = useClientReviewsQuery({
    startDate: formatDate(addYears(new Date(), -1)),
    endDate: formatDate(new Date()),
    ...getReviewServiceDepartment(name),
    limit: 3,
  });

  const safeGetTime = (date?: string, startTime?: string) => {
    if (date && startTime) return `${date}T${startTime}`;
    return "";
  };

  const packageData = confirmationData?.clientConfirmReservation?.package;
  const repeatEvery = packageData?.packageDetail?.repeatEvery;
  const isAdHoc = repeatEvery === PackageRecurrenceEnum.AdHoc;
  const confirmedWorker = packageData?.jobs?.[0]?.worker;
  const allVisits = packageData?.jobs?.flatMap((job) => job.visits ?? []) ?? [];
  const shuffledReview = [...reviewsList].sort(() => Math.random() - 0.5);
  const resolvedVisits = getFragmentData(VisitClientFragmentFragmentDoc, allVisits);
  const visits = resolvedVisits.map((visit) => mapToVisitDetailData(visit));

  const calendarEvent: AddToCalendarProps = {
    title: reviewsList?.[0]?.service ?? "-",
    description: bookingInfo?.note ?? "-",
    startTime: safeGetTime(selectedSlot?.date, selectedSlot?.startTime),
    endTime: safeGetTime(selectedSlot?.date, selectedSlot?.endTime),
    location: bookingInfo?.fullAddress ?? "-",
    visits,
  };

  const isLoading = confirmationLoading || reviewsLoading;

  return (
    <BookingCompleteScreen
      calendarEvent={calendarEvent}
      reviews={shuffledReview}
      visits={[visits[0]]}
      isLoading={isLoading}
      isAdHoc={isAdHoc}
      cleanerInfo={{
        name: `${confirmedWorker?.firstName || ""} ${confirmedWorker?.lastName || ""}`,
        star: String(confirmedWorker?.workerRating || 5),
        avatar: confirmedWorker?.avatarUrl || "",
      }}
    />
  );
};
